import { useEffect, useState, type ReactNode } from 'react'
import { Gift, Keyboard, Lock, PanelLeft, Settings, Smartphone, Ticket, X } from 'lucide-react'
import { Logo } from '../onderdelen/Logo'

/*
 * Het beginscherm van de tablet: foto's uit de bibliotheek in Beheer die
 * langzaam wisselen, in willekeurige volgorde, en onderaan alleen de twee dingen die een klant hier komt doen:
 * sparen of punten verzilveren. Het zijmenu (knop linksonder) heeft de rest;
 * welke knoppen erin staan kiest een medewerker in Instellingen.
 */

export type MenuKnop = 'beloningen' | 'vouchers' | 'code' | 'telefoon'
export const MENU_KNOPPEN: { k: MenuKnop; naam: string; icoon: ReactNode }[] = [
  { k: 'beloningen', naam: 'Alle beloningen', icoon: <Gift size={26} /> },
  { k: 'vouchers', naam: 'Voucher gebruiken', icoon: <Ticket size={26} /> },
  { k: 'code', naam: 'Code typen', icoon: <Keyboard size={26} /> },
  { k: 'telefoon', naam: 'Punten op je telefoon', icoon: <Smartphone size={26} /> },
]
const MENU = 'tp-tablet-menu'

export function leesMenu(): MenuKnop[] {
  try {
    const m = JSON.parse(localStorage.getItem(MENU) ?? 'null') as unknown
    if (Array.isArray(m)) return MENU_KNOPPEN.map((x) => x.k).filter((k) => m.includes(k))
  } catch { /* standaard */ }
  return MENU_KNOPPEN.map((x) => x.k)
}
export function bewaarMenu(m: MenuKnop[]) {
  try { localStorage.setItem(MENU, JSON.stringify(m)) } catch { /* privévenster */ }
}

/** Hoe lang één foto blijft staan. */
const FOTO_MS = 8000

export function Thuis({ kop, fotos, geblokkeerd, spaar, verzilver, menu, kiesMenu, instellingen }: {
  kop: ReactNode; fotos: string[]; geblokkeerd: boolean
  spaar: () => void; verzilver: () => void
  menu: MenuKnop[]; kiesMenu: (k: MenuKnop) => void; instellingen: () => void
}) {
  const [open, zetOpen] = useState(false)
  return (
    <div className="thuis">
      <Fotoreeks fotos={fotos} />
      <div className="thuis-waas" />
      <div className="thuis-kop">{kop}</div>

      <div className="thuis-midden">
        {!fotos.length && <div className="thuis-logo"><Logo /></div>}
        <h1>Spaar bij elke <span>wasbeurt</span></h1>
      </div>

      {geblokkeerd && (
        <div className="thuis-blok"><Lock size={20} /><span>Inwisselen staat uit na te veel foute PIN's. Een manager geeft het vrij in Beheer. Sparen werkt gewoon.</span></div>
      )}

      <div className="thuis-balk">
        <button className="thuis-menuknop" aria-label="Menu" onClick={() => zetOpen(true)}><PanelLeft size={30} /></button>
        <div className="thuis-knoppen">
          <button className="thuis-spaar" onClick={spaar}>Spaar</button>
          <button className="thuis-verzilver" onClick={verzilver}>Punten verzilveren</button>
        </div>
      </div>

      {open && (
        <div className="zijmenu-sluier" onClick={() => zetOpen(false)}>
          <nav className="zijmenu" onClick={(e) => e.stopPropagation()}>
            <button className="zijmenu-dicht" aria-label="Sluiten" onClick={() => zetOpen(false)}><X size={28} /></button>
            {MENU_KNOPPEN.filter((m) => menu.includes(m.k)).map((m) => (
              <button key={m.k} className="zijmenu-knop" onClick={() => { zetOpen(false); kiesMenu(m.k) }}>{m.icoon}<span>{m.naam}</span></button>
            ))}
            <button className="zijmenu-knop" onClick={() => { zetOpen(false); instellingen() }}><Settings size={26} /><span>Instellingen</span></button>
          </nav>
        </div>
      )}
    </div>
  )
}

/** Een nieuwe willekeurige volgorde (Fisher-Yates), niet beginnend met `niet`. */
function geschud(lijst: string[], niet?: string): string[] {
  const u = [...lijst]
  for (let i = u.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[u[i], u[j]] = [u[j]!, u[i]!]
  }
  if (u.length > 1 && u[0] === niet) [u[0], u[1]] = [u[1]!, u[0]!]
  return u
}

/*
 * De foto's: in willekeurige volgorde, na een ronde opnieuw geschud. Telkens
 * één in beeld en de volgende alvast geladen, zodat de tablet niet alle
 * foto's tegelijk binnenhaalt.
 */
function Fotoreeks({ fotos }: { fotos: string[] }) {
  const sleutel = fotos.join('|')
  const [stand, zetStand] = useState(() => ({ volgorde: geschud(fotos), nu: 0, vorige: null as string | null }))
  useEffect(() => { zetStand({ volgorde: geschud(fotos), nu: 0, vorige: null }) }, [sleutel]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (fotos.length < 2) return
    const t = setInterval(() => zetStand((s) => {
      const huidig = s.volgorde[s.nu] ?? null
      if (s.nu + 1 < s.volgorde.length) return { ...s, nu: s.nu + 1, vorige: huidig }
      return { volgorde: geschud(fotos, huidig ?? undefined), nu: 0, vorige: huidig }
    }), FOTO_MS)
    return () => clearInterval(t)
  }, [sleutel]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!stand.volgorde.length) return <div className="thuis-fotos leeg" />
  const huidig = stand.volgorde[stand.nu]
  const volgende = stand.volgorde[stand.nu + 1] ?? null
  const zien = [...new Set([stand.vorige, huidig, volgende].filter((x): x is string => !!x))]
  return (
    <div className="thuis-fotos">
      {zien.map((url) => <img key={url} src={url} alt="" className={url === huidig ? 'zichtbaar' : ''} decoding="async" />)}
    </div>
  )
}
